import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { PageList, FormLayout } from '../components/PageComponents';
import { usePageAPI } from '../hooks/usePageAPI';
import Head from './layouts/Head';
import LoadingDashboardContent from '../components/LoadingDashboardContent';
import axios from 'axios';
import { API_URL } from '../constants';
import { formatDateTime, idFormat } from '../utils/appUtils';
import { useTokens } from '../context/TokenContext';

const LoginHistory = () => {
  const { getItem } = useTokens();
  const adminAuthToken = getItem('admin', 'AuthToken');
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const { fetchPages } = usePageAPI();
  const [pageData, setPageData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [searchBy, setSearchBy] = useState('user_id');
  const [searchValue, setSearchValue] = useState('');
  const [isReload, setIsReload] = useState(false);

  const pageType = {
    isNew: pathname.includes('new'),
    isEdit: pathname.includes('edit'),
    isList: !(pathname.includes('new') || pathname.includes('edit')),
  };

  useEffect(() => {
    const params = new URLSearchParams(search);
    const page = Number(params.get('page')) || 1;
    const limit = Number(params.get('limit')) || 20;
    const searchBy = params.get('searchBy') || 'user_id';
    const searchValue = params.get('searchValue') || '';

    setCurrentPage(page);
    setLimit(limit);
    setSearchBy(searchBy);
    setSearchValue(searchValue);

    const loadData = () => {
      setLoading(true);
      axios.get(`${API_URL}/login-history`, {
        params: { page, limit, searchBy, searchValue },
        headers: { 'Authorization': `Bearer ${adminAuthToken}` },
      })
        .then(response => {
          setPageData({
            data: response.data.data,
            dataCount: response.data.dataCount,
          });
        })
        .catch(error => {
          console.error('Failed to fetch data:', error);
        })
        .finally(() => {
          setLoading(false);
        });
    };

    if (pageType.isList) loadData();
    
    if (isReload) {
      setIsReload(false);
    }
  }, [pathname, search, isReload]);

  const updateUrlParams = (page, limit, searchBy, searchValue) => {
    const params = new URLSearchParams();
    params.set('page', page);
    params.set('limit', limit);
    params.set('searchBy', searchBy);
    params.set('searchValue', searchValue);
    navigate({ pathname: '/admin/login-history', search: params.toString() });
  };

  return (
    <>
      <Head title={getPageTitle(pageType)} />

      {pageType.isList && (
        loading ? <LoadingDashboardContent progress={progress} /> :
        <PageList
          itemType={'login-history'}
          itemId={'id'}
          pageTitle={'Login History List'}
          pageData={pageData.data}
          actionApplyKeyMap={{
            'Delete': 'delete',
          }}
          columns={['User ID', 'IP Address', 'Device/Browser', 'Location', 'Login Time', 'Login Status']}
          filterBy={{
            '--select--': '',
            'User ID': 'user_id',
            'User Email': 'user_email',
            'IP Address': 'ip_address',
            'Device/Browser': 'device_browser',
            'Login Status': 'status',
            'Location': 'location',
            'DateRange': 'datetime',
          }}
          columnKeyMap={{
            'User ID': (item) => <>{item.user_name} {idFormat(item.user_id)}</>,
            'User Email': (item) => item.user_email,
            'IP Address': (item) => item.ip_address,
            'Device/Browser': (item) => item.device_browser,
            'Location': (item) => item.location === '' ? 'N/A' : item.location,
            'Login Time': (item) => formatDateTime(item.login_time),
            'Login Status': (item) => item.status,
          }}
          onAddNew={false}
          onView={false}
          onEdit={false}
          onDelete={false}
          totalRows={pageData.dataCount}
          updateUrlParams={updateUrlParams}
          isReload={setIsReload}
        />
      )}
    </>
  );
};

const getPageTitle = ({ isEdit, isNew }) =>
  isEdit ? "Edit Login History" : isNew ? "Create New Login History" : "Login History List";

export default LoginHistory;
